import React, { FC, useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetLocationData } from "~/hooks/locationData";
import { onScrollBottomIndicator } from "~/hooks/selectIndicatorValue";
import { ESubType } from "~/pages/Editor/EditorServiceAuthorization/TableReducer/options";
import { Field } from "~/shared/ui/Field";

import s from "./QRatingForm.less";

interface IProps {
  formId: string;
  onSubmit: (baseParams) => void;
  data;
  options?;
  arrToolsInsuranceGroup;
  arrToolsGroup;
  selectPlaces?;
  selectParent;
  isEditable?: boolean;
  selectedService?: string;
}

const ZDEditorForm: FC<IProps> = ({
  formId,
  onSubmit,
  data,
  options = {},
  arrToolsGroup,
  arrToolsInsuranceGroup,
  selectPlaces,
  selectParent,
  isEditable = false,
  selectedService,
}) => {
  const { t } = useTranslation();
  const { currentId, openModal } = useGetLocationData();

  const exceptionsFields = ["ActualFrom"];

  const withoutpParent = {
    value: null,
    label: t("common.without_parent"),
  };

  const arrSelectTrade = ["places", "tradePremises"];

  const [isArrGroup, setIsArrGroup] = useState(true);

  const oneFilter =
    selectParent && openModal !== "Add"
      ? selectParent.filter((el) => el.id !== currentId)
      : selectParent;

  const isParen =
    selectParent &&
    openModal !== "Add" &&
    selectParent.find((e) => e.id === currentId) &&
    selectParent.find((e) => e.id === currentId).isParent;

  const arrPirme = [
    "ComplianceIndicatorId",
    "AreaIndicatorId",
    "PlacesCombineIndicatorId",
    "FactIndicatorId",
    "CertificationDateIndicatorId",
    "FactContractIndicatorId",
    "ModelIndicatorId",
    "TotalIndicatorId",
    "CheckedDateIndicatorId",
    "CountryIndicatorId",
    "YearIndicatorId",
    "NoteIndicatorId",
    "CombineIndicatorId",
    "PersonNameIndicatorId",
    "PositionIndicatorId",
    "PositionStartIndicatorId",
    "StudyCertificateDateIndicatorId",
    "ImageIndicatorId",
    "AddressIndicatorId",
  ];

  const groupTradeOptions = [
    { label: t(`editorZD.showroom`), value: "showroom" },
    { label: t(`editorZD.issuanceZones`), value: "issuanceZones" },
    { label: t(`editorZD.storageSites`), value: "storageSites" },
  ];

  const groupAreaOptions = [
    { label: t(`editorZD.service`), value: "service" },
    { label: t(`editorZD.workshopVR`), value: "workshopVR" },
  ];

  const { loading } = onScrollBottomIndicator();

  const yapShaper = options.reduce((acc, el) => {
    if (el.name === "ActualFrom") return acc;
    if (el.type === "indicator" && el.name !== "GroupOne") {
      if (el.name !== "ParentId") {
        acc[el.name] = yup.object().shape({
          // value: yup.string().nullable().required(t("validation.required")),
        });
      } else {
        acc[el.name] = yup.object().shape({
          value: yup.string().nullable(),
        });
      }
    }

    if (!arrSelectTrade.includes(selectedService) && el.name === "GroupOne") {
      if (isArrGroup) {
        acc["arrGroup"] = yup.object().shape({
          value: yup.string().nullable().required(t("validation.required")),
        });
      } else {
        acc["newGroup"] = yup
          .string()
          .matches(
            /^[a-zA-Zа-яА-ЯёЁ()0-9][a-zA-Zа-яА-ЯёЁ()0-9$!.,/#*\s\-]{0,}$/,
            t("validation.invalid_text"),
          )
          .max(250, t("validation.more250"))
          .required(t("validation.required"));
      }
    }

    if (el.type === "input") {
      if (el.name === "Number" || el.name === "Code") {
        acc[el.name] = yup
          .string()
          .trim()
          .matches(/^[0-9][0-9.\s\-]{0,}$/, t("validation.invalid_text"))
          .max(250, t("validation.more250"))
          .required(t("validation.required"));
      } else if (!arrPirme.includes(el.name)) {
        acc[el.name] = yup
          .string()
          .trim()
          .matches(
            /^[a-zA-Zа-яА-ЯёЁ()0-9][a-zA-Zа-яА-ЯёЁ0-9()$!.,/#*\s\-]{0,}$/,
            t("validation.invalid_text"),
          )
          .max(250, t("validation.more250"))
          .required(t("validation.required"));
      }
    }

    return acc;
  }, {});

  yapShaper["NameEn"] = yup
    .string()
    .trim()
    .max(250, t("validation.more250"))
    .required(t("validation.required"));

  const schema = yup.object().shape(yapShaper);

  const { handleSubmit, control, errors, setValue } = useForm({
    defaultValues: data && {
      ...data,
      arrGroup: { label: data.GroupOne, value: data.GroupOne },
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!!data) {
      Object.keys(data).forEach((el) => {
        const elData =
          el === "ParentId"
            ? selectParent.find((e) => e.value === data[el])
              ? selectParent.find((e) => e.value === data[el])
              : withoutpParent
            : data[el];
        setValue(el, elData);
      });
    }
  }, [openModal, data]);

  const arrIs = ["ParentId", "ZD_PlacesId"];

  const handleFormSubmit = (formData) => {
    const baseParams = Object.keys(formData).reduce((acc, key) => {
      if (
        typeof formData[key] === "object" &&
        !!formData[key] &&
        key !== "newGroup" &&
        key !== "groupTrade" &&
        key !== "groupArea" &&
        key !== "GroupOne" &&
        key !== "arrGroup"
      ) {
        acc[!arrIs.includes(key) ? key.slice(0, -2) : key] =
          formData[key].value;
        return acc;
      }
      if (key === "newGroup" || key === "arrGroup") {
        acc["GroupOne"] =
          key === "arrGroup" ? formData[key].value : formData[key];
        return acc;
      }
      if (arrSelectTrade.includes(selectedService) && key === "GroupOne") {
        acc["GroupOne"] = formData[key].value;
        return acc;
      }

      acc[key] = formData[key];
      return acc;
    }, {});

    onSubmit(baseParams);
  };

  const groupOptions = (
    selectedService === "tools" ? arrToolsGroup : arrToolsInsuranceGroup
  ).map((group) => ({ label: group, value: group }));

  const drawFormByOptions = () => {
    return options.map((el, id) => {
      if (exceptionsFields.includes(el.name)) return null;
      if (el.type === ESubType.indicator) {
        if (!selectPlaces || el.name !== "ZD_PlacesId") {
          if (el.name === "ParentId") {
            return (
              <Field
                key={id + el.name}
                type="select"
                name={el.name}
                label={t(`editorZD.${selectedService}.${el.name}`)}
                control={control}
                loading={loading}
                error={errors[el.name]?.value?.message}
                options={[withoutpParent, ...oneFilter]}
                labelInValue
                disabled={isParen}
              />
            );
          }
          if (
            !arrSelectTrade.includes(selectedService) &&
            el.name === "GroupOne"
          ) {
            return (
              <div key={id + el.name} className={s.groupRow}>
                {isArrGroup ? (
                  <>
                    <Field
                      type="select"
                      name="arrGroup"
                      label={t(`editorZD.${selectedService}.arrGroup`)}
                      control={control}
                      loading={loading}
                      error={errors["arrGroup"]?.value?.message}
                      options={groupOptions}
                      labelInValue
                      showSearch
                      optionFilterProp="label"
                      wrapperClassName={s.groupField}
                    />
                    <Button
                      size={"middle"}
                      variant="outlined"
                      onClick={() => setIsArrGroup(false)}
                    >
                      {t("editorZD.BtnGroup")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Field
                      type="input"
                      name="newGroup"
                      label={t(`editorZD.${selectedService}.newGroup`)}
                      control={control}
                      error={errors["newGroup"]?.message}
                      wrapperClassName={s.groupField}
                    />
                    <Button
                      size={"middle"}
                      variant="outlined"
                      onClick={() => setIsArrGroup(true)}
                    >
                      {t("editorZD.BtnGroupNew")}
                    </Button>
                  </>
                )}
              </div>
            );
          }
        } else {
          return (
            <Field
              key={id + el.name}
              type="select"
              name={el.name}
              label={t(`editorZD.${selectedService}.${el.name}`)}
              control={control}
              loading={loading}
              error={errors[el.name]?.value?.message}
              options={selectPlaces}
              labelInValue
              disabled={!isEditable}
            />
          );
        }
        if (
          selectedService === "tradePremises" &&
          el.name === "GroupOne" &&
          !arrPirme.includes(el.name)
        ) {
          return (
            <Field
              key={id + el.name}
              type="select"
              name={el.name}
              label={t(`editorZD.${selectedService}.${el.name}`)}
              control={control}
              error={errors[el.name]?.value?.message}
              options={groupTradeOptions}
              labelInValue
            />
          );
        }
        if (selectedService === "places" && el.name === "GroupOne") {
          return (
            <Field
              key={id + el.name}
              type="select"
              name={el.name}
              label={t(`editorZD.${selectedService}.${el.name}`)}
              control={control}
              error={errors[el.name]?.value?.message}
              options={groupAreaOptions}
              labelInValue
            />
          );
        }
        return null;
      }

      if (el.type === ESubType.input) {
        return (
          <React.Fragment key={id + el.name}>
            {el.name !== "groupTrade" && el.name !== "groupArea" && (
              <Field
                type="input"
                name={el.name}
                label={t(`editorZD.${selectedService}.${el.name}`)}
                control={control}
                error={errors[el.name]?.message}
                disabled={!isEditable}
              />
            )}
            {el.name === "Name" && (
              <Field
                type="input"
                name="NameEn"
                label={t(`editorZD.${selectedService}.NameEn`)}
                control={control}
                error={errors["NameEn"]?.message}
                disabled={!isEditable}
              />
            )}
          </React.Fragment>
        );
      }
      return null;
    });
  };

  return (
    <form id={formId} onSubmit={handleSubmit(handleFormSubmit)}>
      <Flex vertical gap={10}>
        {drawFormByOptions()}
      </Flex>
    </form>
  );
};
export default ZDEditorForm;
